import Fuse, { IFuseOptions } from 'fuse.js';
import { SecsMessage, SearchResult, DataItemDefinition } from '../types/secs';
import { getAllMessages } from './secsDataLoader';
import { getAllDataItems } from './dataItems';

const searchOptions: IFuseOptions<SecsMessage> = {
  keys: [
    { name: 'id', weight: 3 },
    { name: 'name', weight: 2 },
    { name: 'description', weight: 1.5 },
    { name: 'usage', weight: 1 },
    { name: 'dataItems.name', weight: 1.2 },
    { name: 'dataItems.description', weight: 0.8 },
  ],
  threshold: 0.3,
  includeScore: true,
  includeMatches: true,
  minMatchCharLength: 2,
};

let fuseInstance: Fuse<SecsMessage> | null = null;

function getFuseInstance(): Fuse<SecsMessage> {
  if (!fuseInstance) {
    const messages = getAllMessages();
    fuseInstance = new Fuse(messages, searchOptions);
  }
  return fuseInstance;
}

export function searchMessages(query: string): SearchResult[] {
  if (!query || query.trim().length < 2) {
    return [];
  }

  const trimmed = query.trim();
  const messages = getAllMessages();

  // Exact SxFy match (e.g., S1F1, s2f13)
  const exactPattern = /^S(\d+)F(\d+)$/i;
  const exactMatch = trimmed.match(exactPattern);
  if (exactMatch) {
    const stream = parseInt(exactMatch[1]);
    const func = parseInt(exactMatch[2]);
    return messages
      .filter(m => m.stream === stream && m.function === func)
      .map(item => ({ item, score: 0 }));
  }

  // Partial SxF or Sx match (e.g., S1F, S2)
  const partialPattern = /^S(\d+)F?$/i;
  const partialMatch = trimmed.match(partialPattern);
  if (partialMatch) {
    const stream = parseInt(partialMatch[1]);
    return messages
      .filter(m => m.stream === stream)
      .map(item => ({ item, score: 0 }));
  }

  // Fallback: Fuse.js fuzzy search
  const fuse = getFuseInstance();
  const results = fuse.search(trimmed);

  return results.map(result => ({
    item: result.item,
    score: result.score || 0,
    matches: result.matches as SearchResult['matches'],
  }));
}

let itemFuse: Fuse<DataItemDefinition> | null = null;

function getItemFuse(): Fuse<DataItemDefinition> {
  if (!itemFuse) {
    itemFuse = new Fuse(getAllDataItems(), {
      keys: [
        { name: 'name', weight: 3 },
        { name: 'description', weight: 1 },
        { name: 'codes.meaning', weight: 0.5 },
      ],
      threshold: 0.3,
      minMatchCharLength: 2,
    });
  }
  return itemFuse;
}

export function searchDataItems(query: string): DataItemDefinition[] {
  const q = query.trim();
  if (q.length < 2) return [];
  const exact = getAllDataItems().find(d => d.name.toLowerCase() === q.toLowerCase());
  if (exact) {
    return [exact, ...getItemFuse().search(q).map(r => r.item).filter(d => d.name !== exact.name)];
  }
  return getItemFuse().search(q).map(r => r.item);
}

export function searchByStreamFunction(stream: string, func?: string): SecsMessage[] {
  const messages = getAllMessages();

  return messages.filter(msg => {
    const streamMatch = `S${msg.stream}` === stream.toUpperCase();
    if (!func) return streamMatch;

    const funcMatch = `F${msg.function}` === func.toUpperCase();
    return streamMatch && funcMatch;
  });
}
